import React from "react";
import styled, { css } from "styled-components";
import { ButtonProps, getButtonStyle } from "../../layout/style/colors";
import Pagination from "../Pagination";

export interface TableProps<T> {
  data: T[];
  columns: {
    key: keyof T | "actions" | "check";
    header: string;
    render?: (item: T) => React.ReactNode;
    weight?: number;
    style?: "date" | "name" | "bold" | "default";
  }[];
  checkIndex?: number[];
  setCheckIndex?: any;
  actions?: (item: T) => React.ReactNode;
  checkbox?: (item: T) => React.ReactNode;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick?: (item: T) => void;
  selectedRow?: T | null;
  showIndex?: boolean;
  emptyString?: string;
  isCheck?: (item: T) => boolean;
}

export interface TableHeaderContainerProps {
  $weights: number[];
}

export const TableContainer = styled.div<TableHeaderContainerProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: calc(100% - 34px);
  & > * {
    ${({ $weights }) =>
      $weights
        .map(
          (value, index) => `
        &:nth-child(${index + 1}) {
          flex: ${value};
        }
      `,
        )
        .join("")}
  }
`;

export const TableRowContainer = styled(TableContainer).attrs({
  className: "row",
})<{ $isSelected?: boolean }>`
  height: 41px;
  width: 100%;
  padding: 4px 11px;
  border-radius: 11.47px;
  background-color: #f0f0f0;
  cursor: pointer;
  box-sizing: border-box;

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      background: #7f25fb;
      color: white;

      &:hover {
        background: #7f25fb;
      }
    `}
`;

export const TableItemWrapper = styled.div`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  gap: 10px;
  border-radius: 10px;
`;

export const TableRowButton = styled.div<ButtonProps>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  height: 18px;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 17.9px;
  text-align: center;
  padding: 8px;
  flex: ${({ $weight = 1 }) => $weight};

  ${({ $variant }) => getButtonStyle($variant)}
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      color: black;
      background: white;
    `}
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const TableContent = styled.div`
  flex-grow: 1;
`;
const TableListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const TableHeaderWrapper = styled(TableContainer)`
  padding: 0 17px;
`;

const TableEmptyWrapper = styled.h2`
  text-align: center;
`;

const TableHeaderItemWrapper = styled(TableItemWrapper)`
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 17.9px;
  text-align: left;
`;

const getColumnStyle = (style?: string) => {
  switch (style) {
    case "date":
      return css`
        background: rgba(55, 54, 54, 1);
        color: white;
        border-radius: 10px;
        font-family: Pretendard;
        font-size: 12px;
        font-weight: 500;
        line-height: 17.9px;
        text-align: center;
        padding: 8px;
        width: 95px;
        height: 25px;
        box-sizing: border-box;
      `;
    case "name":
      return css`
        font-family: Pretendard;
        font-size: 15px;
        font-weight: 700;
        line-height: 23.87px;
        text-align: center;
      `;
    case "bold":
      return css`
        font-family: Pretendard;
        font-size: 15px;
        font-weight: 600;
        line-height: 17.9px;
        text-align: center;
      `;
    case "header":
      return css`
        font-family: Pretendard;
        font-size: 15px;
        font-weight: 500;
        line-height: 17.9px;
        text-align: center;
      `;
    default:
      return css`
        font-family: Pretendard;
        font-size: 15px;
        font-weight: 400;
        line-height: 17.9px;
        text-align: center;
      `;
  }
};

const StyledTableItemWrapper = styled(TableItemWrapper)<{ $style?: string }>`
  ${({ $style }) => getColumnStyle($style)}
`;

export const PaginationWrapper = styled.div`
  display: flex;
  padding: 20px 0;
`;

export const TableActions = styled.div`
  flex: 2;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
`;

const CommonTable = <T extends object>({
  data,
  columns,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
  actions,
  selectedRow,
  showIndex,
  emptyString,
  checkIndex,
  checkbox,
  isCheck,
}: TableProps<T>) => {
  const indexColumn = {
    key: "index" as keyof T,
    header: "NO",
    render: (_: T, index: number) => index + 1,
    weight: 0.5,
    style: "bold" as const,
  };
  const weights = columns.map((col) => col.weight || 1);
  const allColumns = showIndex ? [indexColumn, ...columns] : columns;

  return (
    <TableWrapper>
      <TableContent>
        <TableHeaderWrapper $weights={weights}>
          {allColumns.map((column, index) => (
            <TableHeaderItemWrapper key={index}>
              {column.header}
            </TableHeaderItemWrapper>
          ))}
        </TableHeaderWrapper>
        <TableEmptyWrapper>
          {data.length < 1 &&
            (emptyString ? emptyString : "데이터가 없습니다.")}
        </TableEmptyWrapper>
        <TableListWrapper>
          {data.map((item, rowIndex) => (
            <TableRowContainer
              key={rowIndex}
              $weights={weights}
              onClick={() => onRowClick && onRowClick(item)}
              $isSelected={selectedRow === item}
            >
              {allColumns.map((column, colIndex) => (
                <StyledTableItemWrapper key={colIndex} $style={column.style}>
                  {column.key === "actions"
                    ? actions && actions(item)
                    : column.key === "check"
                      ? checkbox && checkbox(item)
                      : column.render
                        ? column.render(item, rowIndex)
                        : column.key === "index"
                          ? rowIndex + 1
                          : String(item[column.key])}
                </StyledTableItemWrapper>
              ))}
            </TableRowContainer>
          ))}
        </TableListWrapper>
      </TableContent>

      {totalPages > 1 && (
        <PaginationWrapper>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </PaginationWrapper>
      )}
    </TableWrapper>
  );
};

export default CommonTable;
